import React, { useState, useEffect, useMemo } from "react";
import { api } from "../../../services/api";
import MediaUploader from "./MediaUploader";
import {
  TemplateSelector,
  TemplatePreview,
  TemplateVariableForm,
} from "../../Templates";
import '../CrmModals.css';


const LeadBulkMessageModal = ({
  channel: initialChannel = "WHATSAPP",
  leads = [],
  onSend,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    channel: initialChannel || "WHATSAPP",
    content: "",
    subject: "",
    apply_anti_block: true,
    apply_anti_detection: true,
    max_per_batch: 50,
    integration_id: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [showAllLeads, setShowAllLeads] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  // Estado para mídia
  const [mediaInfo, setMediaInfo] = useState(null);

  // Estado para integrações
  const [integrations, setIntegrations] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [integrationsError, setIntegrationsError] = useState(null);

  // Estado para Cloud API / Templates
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variableMapping, setVariableMapping] = useState({});

  // Buscar integrações ao montar o modal
  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        setIntegrationsLoading(true);
        setIntegrationsError(null);
        const response = await api.get("/api/integrations");
        const allIntegrations = response.data || [];
        setIntegrations(allIntegrations);
      } catch (err) {
        console.error("Erro ao carregar integrações:", err);
        setIntegrationsError("Não foi possível carregar integrações");
      } finally {
        setIntegrationsLoading(false);
      }
    };

    fetchIntegrations();
  }, []);

  // Filtrar integrações baseado no canal
  const getFilteredIntegrations = () => {
    if (formData.channel === "WHATSAPP") {
      return integrations.filter(
        (i) => i.type === "whatsapp" || i.key === "whatsapp_evolution" || i.key === "whatsapp_cloud_api"
      );
    }
    if (formData.channel === "EMAIL") {
      return integrations.filter(
        (i) => i.type === "email" || i.key === "email"
      );
    }
    return [];
  };

  const filteredIntegrations = getFilteredIntegrations();

  // Detectar se a integração selecionada é Cloud API
  const selectedIntegration = useMemo(() => {
    if (!formData.integration_id) return null;
    return integrations.find((i) => String(i.id) === String(formData.integration_id));
  }, [formData.integration_id, integrations]);

  const isCloudAPI = useMemo(() => {
    if (!selectedIntegration) return false;
    return (
      selectedIntegration.key === "whatsapp_cloud_api" ||
      selectedIntegration.integration_type === "CLOUD_API"
    );
  }, [selectedIntegration]);

  // Limpar template quando mudar de integração
  useEffect(() => {
    if (!isCloudAPI) {
      setSelectedTemplate(null);
      setVariableMapping({});
    }
  }, [formData.integration_id, isCloudAPI]);

  // Auto-selecionar se houver apenas uma integração
  useEffect(() => {
    if (filteredIntegrations.length === 1 && !formData.integration_id) {
      setFormData((prev) => ({
        ...prev,
        integration_id: filteredIntegrations[0].id,
      }));
    } else if (filteredIntegrations.length === 0) {
      setFormData((prev) => ({
        ...prev,
        integration_id: "",
      }));
    }
  }, [filteredIntegrations.length, formData.channel]);

  const isWhatsAppChannel = formData.channel === "WHATSAPP";
  const isEmailChannel = formData.channel === "EMAIL";

  const leadsWithPhone = leads.filter((l) => !!l.phone);
  const leadsWithoutPhone = leads.filter((l) => !l.phone);

  const leadsWithEmail = leads.filter((l) => !!l.email);
  const leadsWithoutEmail = leads.filter((l) => !l.email);

  const validLeads = isEmailChannel ? leadsWithEmail : leadsWithPhone;
  const invalidLeads = isEmailChannel ? leadsWithoutEmail : leadsWithoutPhone;

  const totalLeads = leads.length;
  const validCount = validLeads.length;
  const invalidCount = invalidLeads.length;

  const validate = () => {
    const newErrors = {};

    if (!formData.channel) {
      newErrors.channel = "Canal obrigatório";
    }

    // Validar integração
    if (filteredIntegrations.length > 0 && !formData.integration_id) {
      newErrors.integration = "Selecione uma integração";
    }

    // Validações específicas por tipo de integração
    if (isCloudAPI) {
      // Cloud API requer template
      if (!selectedTemplate) {
        newErrors.template = "Selecione um template para envio via Cloud API";
      }
    } else {
      // Baileys: Permitir mensagem vazia se houver mídia (apenas para WhatsApp)
      if (!formData.content.trim() && !(isWhatsAppChannel && mediaInfo)) {
        newErrors.content = "Mensagem obrigatória";
      }
    }

    if (isEmailChannel) {
      if (!formData.subject.trim()) {
        newErrors.subject = "Assunto obrigatório para email";
      }
      if (validCount === 0) {
        newErrors.recipients =
          "Nenhum lead válido com email para envio em massa.";
      }
    } else if (isWhatsAppChannel) {
      if (validCount === 0) {
        newErrors.recipients =
          "Nenhum lead válido com telefone para envio em massa.";
      }
    } else {
      newErrors.channel = "Canal inválido para envio em massa.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handlers para mídia
  const handleMediaUpload = (media) => {
    setMediaInfo(media);
  };

  const handleMediaRemove = () => {
    setMediaInfo(null);
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById("bulk-message-content");
    if (!textarea) {
      setFormData((prev) => ({
        ...prev,
        content: (prev.content || "") + variable,
      }));
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content || "";
    const newText = text.substring(0, start) + variable + text.substring(end);

    setFormData((prev) => ({
      ...prev,
      content: newText,
    }));

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + variable.length,
        start + variable.length
      );
    }, 0);
  };

  const getPreviewForLead = (lead) => {
    if (!lead) return formData.content || "";

    const replacements = {
      "{{name}}": lead.name || "Nome do lead",
      "{{email}}": lead.email || "email@exemplo.com",
      "{{phone}}": lead.phone || "+5511999999999",
      "{{company}}": lead.company || "Empresa",
    };

    let preview = formData.content || "";
    Object.entries(replacements).forEach(([token, value]) => {
      const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      preview = preview.replace(new RegExp(escaped, "g"), value);
    });

    return preview;
  };

  const handleSend = async () => {
    if (!validate()) return;

    try {
      setSending(true);

      let payload;

      const integrationId = formData.integration_id ? Number(formData.integration_id) : null;


      if (isWhatsAppChannel) {
        const recipients = leadsWithPhone.map((lead) => ({
          phone: lead.phone,
          name: lead.name,
          lead_id: lead.id,
          variables: {
            name: lead.name,
            email: lead.email,
            phone: lead.phone,
            company: lead.company,
          },
        }));

        if (isCloudAPI && selectedTemplate) {
          // Payload para Cloud API com template
          payload = {
            channel: "WHATSAPP",
            use_template: true,
            template: {
              name: selectedTemplate.name,
              language: selectedTemplate.language || "pt_BR",
            },
            variable_mapping: variableMapping,
            recipients,
            apply_anti_block: formData.apply_anti_block,
            max_per_batch: formData.max_per_batch,
            integration_id: integrationId,
          };
        } else {
          // Payload para Baileys (mensagem livre)
          payload = {
            channel: "WHATSAPP",
            message_template: formData.content,
            recipients,
            apply_anti_block: formData.apply_anti_block,
            max_per_batch: formData.max_per_batch,
            integration_id: integrationId,
            media_id: mediaInfo?.id || null,
          };
        }
      } else if (isEmailChannel) {
        const recipients = leadsWithEmail.map((lead) => ({
          email: lead.email,
          name: lead.name,
          variables: {
            name: lead.name,
            email: lead.email,
            phone: lead.phone,
            company: lead.company,
          },
        }));

        payload = {
          channel: "EMAIL",
          subject: formData.subject.trim(),
          message_template: formData.content,
          recipients,
          apply_anti_detection: formData.apply_anti_detection,
          max_per_batch: formData.max_per_batch,
          integration_id: integrationId,
        };
      } else {
        throw new Error("Canal inválido para envio em massa.");
      }

      if (onSend) {
        await onSend(payload);
      } else {
        console.log("Bulk send payload (fallback):", payload);
      }

      setSending(false);
      onClose();
    } catch (err) {
      console.error(err);
      setSending(false);
      setErrors((prev) => ({
        ...prev,
        global:
          "Erro ao enviar mensagens em massa. Tente novamente em instantes.",
      }));
    }
  };

  const previewLead =
    validLeads[previewIndex] || validLeads[0] || leads[0] || null;

  const channelLabel = isWhatsAppChannel
    ? "WhatsApp (envio em massa)"
    : isEmailChannel
      ? "Email (envio em massa)"
      : formData.channel;

  const validContactLabel = isEmailChannel
    ? "email válido"
    : "telefone válido";

  const missingContactLabel = isEmailChannel ? "Sem email" : "Sem phone";

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div
        className="crm-modal crm-modal-large"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="crm-modal-header">
          <div className="crm-modal-header-content">
            <h3 className="crm-modal-title">Enviar mensagem em massa</h3>
            <p className="crm-modal-subtitle crm-mt-4">
              {totalLeads} lead
              {totalLeads !== 1 && "s"} selecionado
              {totalLeads !== 1 && "s"} · {validCount} com{" "}
              {validContactLabel}
              {invalidCount > 0 &&
                ` · ${invalidCount} sem ${isEmailChannel ? "email" : "telefone"} (serão ignorados)`}
            </p>
          </div>
          <button onClick={onClose} className="crm-modal-close">
            &times;
          </button>
        </div>

        <div className="crm-modal-body">
          <div
            className="crm-form-grid-responsive crm-bulk-message-grid"
          >
            <div>
              <div className="crm-flex-row crm-justify-between crm-mb-2">
                <strong className="crm-text-primary">Leads selecionados</strong>
                {totalLeads > 5 && (
                  <button
                    type="button"
                    onClick={() => setShowAllLeads((prev) => !prev)}
                    className="crm-btn crm-btn-secondary crm-btn-xs"
                  >
                    {showAllLeads
                      ? "Mostrar menos"
                      : `Ver todos (${totalLeads})`}
                  </button>
                )}
              </div>

              <div className="crm-list-container">
                {leads.map((lead, index) => {
                  const hasContact = isEmailChannel
                    ? !!lead.email
                    : !!lead.phone;

                  const isPreview =
                    previewLead && previewLead.id === lead.id;

                  return (
                    <div
                      key={lead.id}
                      onClick={() => setPreviewIndex(index)}
                      className={`crm-list-item ${isPreview ? 'selected' : ''}`}
                    >
                      <div>
                        <div className="crm-list-item-title">
                          {lead.name || "Sem nome"}
                          {lead.company && ` · ${lead.company}`}
                        </div>
                        <div className="crm-list-item-subtitle">
                          {isEmailChannel
                            ? lead.email || "Sem email"
                            : lead.phone || "Sem telefone"}
                        </div>
                      </div>
                      {!hasContact && (
                        <span className="crm-badge-warning">
                          {missingContactLabel}
                        </span>
                      )}
                    </div>
                  );
                })}

                {leads.length === 0 && (
                  <div className="crm-empty-list-message">
                    Nenhum lead selecionado.
                  </div>
                )}
              </div>

              {errors.recipients && (
                <div
                  className="crm-error-message crm-error-message-sm"
                >
                  {errors.recipients}
                </div>
              )}
            </div>

            <div>
              <div className="crm-form-field crm-mb-5">
                <label className="crm-form-label">Canal</label>
                <input
                  type="text"
                  readOnly
                  value={channelLabel}
                  className={`crm-input crm-input-disabled ${errors.channel ? "crm-input-error" : ""}`}
                />
                {errors.channel && (
                  <span className="crm-error-message">{errors.channel}</span>
                )}
              </div>

              <div className="crm-form-field crm-mb-5">
                <label className="crm-form-label">Integração *</label>
                {integrationsLoading ? (
                  <div className="crm-text-muted-sm">Carregando integrações...</div>
                ) : integrationsError ? (
                  <div className="crm-error-message">{integrationsError}</div>
                ) : filteredIntegrations.length === 0 ? (
                  <div className="crm-error-message">
                    Nenhuma integração de {isWhatsAppChannel ? "WhatsApp" : "Email"} configurada
                  </div>
                ) : (
                  <>
                    <select
                      value={formData.integration_id}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          integration_id: e.target.value,
                        }))
                      }
                      className={`crm-select ${errors.integration ? "crm-input-error" : ""}`}
                    >
                      {filteredIntegrations.length > 1 && (
                        <option value="">Selecione uma integração</option>
                      )}
                      {filteredIntegrations.map((integration) => (
                        <option key={integration.id} value={integration.id}>
                          {integration.label || integration.name}
                        </option>
                      ))}
                    </select>
                    {errors.integration && (
                      <span className="crm-error-message">{errors.integration}</span>
                    )}
                  </>
                )}
              </div>

              {isEmailChannel && (
                <div className="crm-form-field crm-mb-5">
                  <label className="crm-form-label">Assunto *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        subject: e.target.value,
                      }))
                    }
                    placeholder="Assunto do email"
                    className={`crm-input crm-input-disabled ${errors.subject ? "crm-input-error" : ""}`}
                  />
                  {errors.subject && (
                    <span className="crm-error-message">{errors.subject}</span>
                  )}
                </div>
              )}

              {/* Cloud API Info Box e Template Selector */}
              {isWhatsAppChannel && isCloudAPI && (
                <>
                  <div className="cloud-api-info-box">
                    <div className="cloud-api-info-box-title">
                      API Oficial do WhatsApp
                    </div>
                    <div className="cloud-api-info-box-content">
                      <p>
                        Esta integração utiliza a API Oficial da Meta. Para iniciar
                        conversas, é obrigatório o uso de Templates previamente
                        aprovados pela Meta.
                      </p>
                      <p>
                        Selecione um template aprovado abaixo para continuar.
                      </p>
                    </div>
                  </div>

                  <div className="crm-form-field crm-mb-5">
                    <label className="crm-form-label">Template *</label>
                    <TemplateSelector
                      integrationId={formData.integration_id}
                      onSelect={setSelectedTemplate}
                      selectedTemplate={selectedTemplate}
                      showFilters={true}
                      showSyncButton={true}
                    />
                    {errors.template && (
                      <span className="crm-error-message">{errors.template}</span>
                    )}
                  </div>

                  {selectedTemplate && (
                    <>
                      <div className="crm-form-field crm-mb-5">
                        <label className="crm-form-label">Preview do Template</label>
                        <TemplatePreview
                          template={selectedTemplate}
                          variableValues={variableMapping}
                        />
                      </div>

                      {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                        <div className="crm-form-field crm-mb-5">
                          <TemplateVariableForm
                            template={selectedTemplate}
                            initialMapping={variableMapping}
                            onChange={setVariableMapping}
                            leads={validLeads}
                            showPreview={true}
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Media Uploader - apenas para WhatsApp Baileys */}
              {isWhatsAppChannel && !isCloudAPI && (
                <div className="crm-form-field crm-mb-5">
                  <label className="crm-form-label">Anexar mídia (opcional)</label>
                  <MediaUploader
                    onUpload={handleMediaUpload}
                    onRemove={handleMediaRemove}
                    mediaInfo={mediaInfo}
                    disabled={sending}
                  />
                  <div className="crm-form-hint">
                    A mesma mídia será enviada para todos os destinatários
                  </div>
                </div>
              )}

              {/* Campo de mensagem livre - apenas para Baileys ou Email */}
              {(!isWhatsAppChannel || !isCloudAPI) && (
                <div className="crm-form-field crm-mb-5">
                  <label className="crm-form-label">
                    Mensagem {(isWhatsAppChannel && mediaInfo) ? "(opcional)" : "*"}
                  </label>
                  <div className="template-variables">
                    <span>Inserir variáveis:</span>
                    <button
                      type="button"
                      onClick={() => insertVariable("{{name}}")}
                    >
                      Nome
                    </button>
                    <button
                      type="button"
                      onClick={() => insertVariable("{{email}}")}
                    >
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => insertVariable("{{phone}}")}
                    >
                      Telefone
                    </button>
                    <button
                      type="button"
                      onClick={() => insertVariable("{{company}}")}
                    >
                      Empresa
                    </button>
                  </div>
                  <textarea
                    id="bulk-message-content"
                    value={formData.content}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        content: e.target.value,
                      }))
                    }
                    className={`crm-textarea ${errors.content ? "crm-input-error" : ""}`}
                    placeholder={mediaInfo ? "Legenda da mídia (opcional)" : "Digite sua mensagem modelo aqui. Use variáveis como {{name}} e {{company}}."}
                    rows="6"
                  />
                  {errors.content && (
                    <span className="crm-error-message">{errors.content}</span>
                  )}
                </div>
              )}

              <div className="crm-flex-center-gap-sm crm-mb-3">
                <label className="crm-checkbox-label">
                  <input
                    type="checkbox"
                    checked={
                      isEmailChannel
                        ? formData.apply_anti_detection
                        : formData.apply_anti_block
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        apply_anti_block: e.target.checked,
                        apply_anti_detection: e.target.checked,
                      }))
                    }
                  />
                  {isEmailChannel
                    ? "Aplicar proteção anti-spam"
                    : "Aplicar proteção anti-bloqueio"}
                </label>

                <div className="crm-text-muted-sm">
                  Lote máximo:{" "}
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={formData.max_per_batch}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        max_per_batch: Number(e.target.value) || 1,
                      }))
                    }
                    className="crm-input crm-input-xs"
                  />
                </div>
              </div>

              {/* Preview para Baileys / Email */}
              {!isCloudAPI && formData.content && previewLead && (
                <div className="crm-message-preview">
                  <strong className="crm-text-strong-block">
                    Pré-visualização para:{" "}
                    {previewLead.name || "Lead selecionado"}
                  </strong>
                  <div className="crm-preview-content">
                    {getPreviewForLead(previewLead)}
                  </div>
                </div>
              )}

              {errors.global && (
                <div
                  className="crm-error-message"
                  style={{ marginTop: 12, fontSize: 13 }}
                >
                  {errors.global}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="crm-modal-footer">
          <button onClick={onClose} className="crm-btn crm-btn-secondary">
            Cancelar
          </button>
          <button
            onClick={handleSend}
            className="crm-btn crm-btn-primary"
            disabled={sending || validCount === 0}
          >
            {sending
              ? "Enviando..."
              : `Enviar para ${validCount} lead${validCount !== 1 ? "s" : ""
              }`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadBulkMessageModal;
